import { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Edit3, Users, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface ProjectDropdownProps {
  project: {
    id: string;
    name: string;
    role?: string;
    starred?: boolean;
  };
  onRename: () => void;
  onMembers: () => void;
  onUnstar?: () => void;
  disabled?: boolean;
}

export default function ProjectDropdown({
  project,
  onRename,
  onMembers,
  onUnstar,
  disabled = false,
}: ProjectDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭菜单
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        !buttonRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ESC 键关闭
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const handleItemClick = (callback: () => void) => {
    callback();
    setIsOpen(false);
  };

  const canRename = project.role === 'owner' || project.role === 'manager';

  const MENU_WIDTH = 160; // w-40

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // 默认向左内展开：菜单右边缘对齐按钮右边缘（朝侧边栏内部）
      // 若左侧空间不足（菜单超出屏幕左边缘），则向右外展开
      let left = rect.right - MENU_WIDTH;
      if (left < 8) {
        left = rect.right + 4;
      }
      setMenuPos({ top: rect.bottom + 4, left });
    }
    setIsOpen(true);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={openMenu}
        disabled={disabled}
        className="p-1.5 rounded text-gray-500 hover:bg-[#e8e6dc] hover:text-gray-700 transition-colors"
      >
        <MoreHorizontal size={12} />
      </button>

      {/* 自定义下拉菜单 - fixed 定位，避免被 overflow:hidden 裁切 */}
      {isOpen && menuPos && (
        <div
          ref={menuRef}
          className="fixed w-40 bg-white rounded-md border shadow-lg z-[9999]"
          style={{ top: menuPos.top, left: menuPos.left }}
          onClick={e => e.stopPropagation()}
        >
          <div className="py-1">
            {/* Rename */}
            <button
              onClick={() => handleItemClick(onRename)}
              disabled={!canRename}
              className={cn(
                'w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                !canRename && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Edit3 size={12} />
              {t('common.rename')}
            </button>

            {/* Members */}
            <button
              onClick={() => handleItemClick(onMembers)}
              className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-gray-100 transition-colors"
            >
              <Users size={12} />
              {t('common.members')}
            </button>

            {/* Unstar */}
            {project.starred && onUnstar && (
              <button
                onClick={() => handleItemClick(onUnstar)}
                className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-gray-100 transition-colors"
              >
                <Star size={12} className="text-yellow-500" />
                {t('common.unstar')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
